import React, { useEffect, useState } from "react";
import {
  Button,
  EmailChips,
  Modal,
  fieldLabelClasses,
} from "../../../components/ui";
import { fetchClient } from "../../clients/services/clientsService";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { formatDuration, formatExerciseDate } from "../exerciseMapping";
import type {
  ExercisePlanSummary,
  PendingExercisePlan,
} from "../types/exercisePlans.types";

export interface SendExerciseModalProps {
  /**
   * A stored plan, off the list — or one not written yet, straight from the
   * form. The second has no `id`, and no name or date either: the backend
   * gives it those on the write, which is what the Send button here starts.
   */
  plan: ExercisePlanSummary | PendingExercisePlan;
  /**
   * Cancel. On a plan not written yet this goes back to the form, with
   * everything typed still in it — nothing has happened that cannot be
   * walked away from.
   */
  onClose: () => void;
  /**
   * Given the addresses it is going to — sending it is the caller's to do,
   * and on a plan not written yet, writing it first.
   *
   * Awaited: the dialog stands with its Send button in flight while the call
   * runs, and stays open if it is refused, so the addresses that were typed
   * are still there to try again with. Closing it is the caller's too, once
   * the plan has actually gone.
   */
  onSend: (emails: string[]) => void | Promise<void>;
}

/** Whether the dialog is on a stored plan — one the backend has named. */
const isStored = (
  plan: ExercisePlanSummary | PendingExercisePlan,
): plan is ExercisePlanSummary => "id" in plan;

/**
 * What the plan is, and where it is about to go.
 *
 * The details are shown rather than asked for: everything bar the address is
 * settled by the time this opens, and reading it back is the last chance to
 * notice the plan was written for the wrong client. The same dialog the meal
 * plan has, minus the download — there is no PDF endpoint for an exercise
 * plan, and a button that could not produce a file would be worse than none.
 *
 * On a plan straight from the form it is the step before the write as well as
 * before the send: the two happen together on Send, so Cancel here leaves
 * nothing behind and goes back to the form. A plan the backend has not named
 * yet has no name or date to read back, so those two lines are left off.
 */
const SendExerciseModal: React.FC<SendExerciseModalProps> = ({
  plan,
  onClose,
  onSend,
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [sending, setSending] = useState(false);

  const getToken = useTokenGetter();

  /**
   * The client's own address, filled in from their record.
   *
   * Fetched rather than carried on the plan: a plan knows who it is for and
   * what they are called, and nothing more about them. It arrives as a chip
   * like any other — removable, and joined by whoever else the plan is being
   * copied to — so the box is a starting point rather than an answer.
   *
   * A client with no address on record, or a lookup that fell over, simply
   * leaves it empty: an address that could not be filled in is a convenience
   * that was not there, not a failure worth a message. Anything already typed
   * wins, since the fetch may land after someone has started.
   */
  useEffect(() => {
    if (!plan.clientId) return;

    const controller = new AbortController();

    fetchClient(getToken, plan.clientId, controller.signal)
      .then((client) => {
        const email = client.email?.trim();
        if (controller.signal.aborted || !email) return;
        setEmails((current) => (current.length === 0 ? [email] : current));
      })
      .catch(() => {});

    return () => controller.abort();
  }, [getToken, plan.clientId]);

  const handleSend = async () => {
    setSending(true);
    try {
      await onSend(emails);
    } finally {
      // Whatever happened, the button is a button again. The dialog itself is
      // only closed by the caller, and only once the plan has gone.
      setSending(false);
    }
  };

  const stored = isStored(plan);
  const exerciseCount = stored ? plan.exerciseCount : plan.entries.length;
  const minutes = stored
    ? plan.minutes
    : plan.entries.reduce((total, entry) => total + entry.minutes, 0);

  const details: Array<[label: string, value: string]> = stored
    ? [
        ["Client name", plan.clientName],
        ["Plan name", plan.name],
        ["Exercises", String(exerciseCount)],
        ["Total duration", formatDuration(minutes)],
        ["Created date", formatExerciseDate(plan.createdOn)],
      ]
    : [
        ["Client name", plan.clientName],
        ["Exercises", String(exerciseCount)],
        ["Total duration", formatDuration(minutes)],
      ];

  return (
    <Modal
      open
      title="Send exercise to client"
      onClose={onClose}
      className="max-w-[526px]"
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="md"
            // Nowhere to send it to is not a send.
            disabled={emails.length === 0}
            loading={sending}
            onClick={handleSend}
          >
            Send
          </Button>
        </>
      }
    >
      {/* Label, colon and value in three columns, so the values line up down
          the dialog however long the labels get. */}
      <dl className="grid grid-cols-[110px_10px_1fr] gap-y-3 text-[14px]">
        {details.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt className="text-ink-muted">{label}</dt>
            <dd aria-hidden="true" className="text-ink-muted">
              :
            </dd>
            <dd className="break-words text-ink">{value}</dd>
          </React.Fragment>
        ))}
      </dl>

      {/* Only where it is true, and said as a fact rather than as a warning:
          sending again is an ordinary thing to do, and the one thing worth
          knowing is that the client has had this before. */}
      {stored && plan.lastSentAt && (
        <p className="mt-4 text-[13px] text-ink-muted">
          Last sent on {formatExerciseDate(plan.lastSentAt)}.
        </p>
      )}

      <div className="mt-5">
        <label className={`${fieldLabelClasses} mb-1.5 block`}>Send to</label>
        <EmailChips emails={emails} onChange={setEmails} />
      </div>
    </Modal>
  );
};

export default SendExerciseModal;
